/**
 * Season service for fetching season period configuration
 * Requirements: 1.1, 1.5
 */

import { get } from "./base.service";
import { API_ENDPOINTS } from "../constants/endpoints";
import type {
  SeasonConfigItem,
  SeasonConfigResponse,
  SeasonPeriod,
} from "../types/season";

/**
 * Parses season config items from API response into a SeasonPeriod object
 * Returns null if data is invalid or missing required items
 */
function parseSeasonConfig(items: SeasonConfigItem[]): SeasonPeriod | null {
  if (!items || !Array.isArray(items)) {
    return null;
  }

  const startItem = items.find((item) => item.config_title === "season_start");
  const endItem = items.find((item) => item.config_title === "season_end");

  if (!startItem || !endItem) {
    return null;
  }

  const seasonStart = new Date(startItem.config_value);
  const seasonEnd = new Date(endItem.config_value);

  // Validate parsed dates
  if (isNaN(seasonStart.getTime()) || isNaN(seasonEnd.getTime())) {
    return null;
  }

  // Validate date range (end should not be before start)
  if (seasonEnd < seasonStart) {
    return null;
  }

  return { seasonStart, seasonEnd };
}

/**
 * Fetches the season period configuration from the API
 * Returns null on failure (network error, invalid response, etc.)
 * This allows the calling code to fall back to showing the booking form
 */
export async function getSeasonPeriod(): Promise<SeasonPeriod | null> {
  try {
    const response = await get(API_ENDPOINTS.SEASON_PERIOD);

    if (!response.success) {
      return null;
    }

    const data = response.data as SeasonConfigResponse["data"];

    if (!data?.items) {
      return null;
    }

    return parseSeasonConfig(data.items);
  } catch {
    // Return null on any error - fallback to showing booking form
    return null;
  }
}
